import intizaLogoPng from "../../assets/img/intizaLogoWhite.png";
import { Link } from "react-router-dom";
import { Button } from "@redelair-sa/intiza-ui";
import { TranslationService } from "../../services/TranslationService";
import ModalService from "./bootstrap/modal/Modal";
import Label from "./form/Label";
import { RequiredManager, ValidationMessage } from "./RequieredManager";
import { ContactService } from "../../services/ContactService";
import { handleErrorWithToast } from "../../utils/RequestUtils";
import { ToastService } from "./bootstrap/Toast";
import "./footer.css";

type FooterProps = {
  /** "minimal": footer claro sin logo, para el login nuevo (flag "newLogin") */
  variant?: "default" | "minimal";
};

const Footer = ({ variant = "default" }: FooterProps) => {
  const year = new Date().getFullYear();

  const OpenTechnicalSupportModal = () => {
    const requiredManager = new RequiredManager();
    const message = {
      name: "",
      firstname: "",
      lastname: "",
      email: "",
      company: "",
      country: "",
      phone: "",
      message: ""
    };
    const onSubmit = async () => {
      if (!requiredManager.validate()) {
        return;
      }
      message.name = message.firstname + " " + message.lastname;
      await handleErrorWithToast(ContactService.sendMessage(message));
      ToastService.showToast(
        TranslationService.translate.OnContactMessage,
        TranslationService.translate.OnContactTitle,
        "success"
      );
      ModalService.hideModal();
    };
    ModalService.showDefaultModal({
      title: TranslationService.translate.ContactSupport,
      hideClose: true,
      message: () => {
        const change =
          <T extends keyof typeof message>(key: T) =>
          (value: string) =>
            (message[key] = value);
        const nameChange = requiredManager.makeRequiredWithId(
          change("firstname"),
          "firstname"
        );
        const lastNameChange = requiredManager.makeRequiredWithId(
          change("lastname"),
          "lastname"
        );
        const emailChange = requiredManager.makeRequiredWithId(
          change("email"),
          "email"
        );
        const countryChange = requiredManager.makeRequiredWithId(
          change("country"),
          "country"
        );
        const phoneChange = requiredManager.makeRequiredWithId(
          change("phone"),
          "phone"
        );
        const messageChange = requiredManager.makeRequiredWithId(
          change("message"),
          "message"
        );
        return (
          <>
            <div className="">
              <Label label={TranslationService.translate.Name}>
                <input
                  className="form-control"
                  onChange={(e) => nameChange(e.target.value)}
                />
                <ValidationMessage onChange={nameChange} />
              </Label>
              <Label label={TranslationService.translate.LastName}>
                <input
                  className="form-control"
                  onChange={(e) => lastNameChange(e.target.value)}
                />
                <ValidationMessage onChange={lastNameChange} />
              </Label>
              <Label label={TranslationService.translate.Email}>
                <input
                  className="form-control"
                  onChange={(e) => emailChange(e.target.value)}
                />
                <ValidationMessage onChange={emailChange} />
              </Label>
              <Label label={TranslationService.translate.Company}>
                <input
                  className="form-control"
                  onChange={(e) => change("company")(e.target.value)}
                />
              </Label>
              <Label label={TranslationService.translate.Country}>
                <input
                  className="form-control"
                  onChange={(e) => countryChange(e.target.value)}
                />
                <ValidationMessage onChange={countryChange} />
              </Label>
              <Label label={TranslationService.translate.Phone}>
                <input
                  className="form-control"
                  onChange={(e) => phoneChange(e.target.value)}
                />
                <ValidationMessage onChange={phoneChange} />
              </Label>
              <Label label={TranslationService.translate.Message}>
                <textarea
                  className="form-control"
                  onChange={(e) => messageChange(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                  translate="no"
                />
                <ValidationMessage onChange={messageChange} />
              </Label>
            </div>
            <div className="modal-footer px-0">
              <Button
                shape="default"
                variant="secondary"
                data-bs-dismiss="modal"
              >
                {TranslationService.translate.Close}
              </Button>
              <Button shape="default" variant="primary" onClick={onSubmit}>
                {TranslationService.translate.Send}
              </Button>
            </div>
          </>
        );
      }
    });
  };

  const openSupport = (e: React.MouseEvent) => {
    e.preventDefault();
    OpenTechnicalSupportModal();
  };

  const links = [
    {
      key: "support",
      node: (
        <a href="/" onClick={openSupport}>
          {TranslationService.translate.ContactSupport}
        </a>
      )
    },
    {
      key: "help",
      node: (
        <a href="https://ayuda.intiza.com" target="_blank" rel="noreferrer">
          {TranslationService.translate.Help}
        </a>
      )
    },
    {
      key: "info",
      node: (
        <Link to={`/${TranslationService.currentLanguage}/home/contact`}>
          {TranslationService.translate.ContactInfo}
        </Link>
      )
    }
  ];

  const socialLinks = (linkClassName: string, xIconSize: number) => (
    <>
      <a
        href="https://www.linkedin.com/company/intiza/"
        target="_blank"
        rel="noreferrer"
        className={linkClassName}
        aria-label="LinkedIn"
      >
        <i className="fa-brands fa-linkedin"></i>
      </a>
      <a
        href="https://x.com/IntizaSaaS"
        rel="noreferrer"
        className={linkClassName}
        aria-label="X"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={xIconSize}
          height={xIconSize}
          fill="currentColor"
          className="bi bi-twitter-x"
          viewBox="0 0 20 20"
        >
          <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
        </svg>
      </a>
    </>
  );

  const copyright = (
    <>
      Copyright {year} | Intiza S.A. {TranslationService.translate.Rights}
    </>
  );

  if (variant === "minimal") {
    return (
      <footer className="footer-minimal">
        <div className="footer-minimal__top">
          <ul className="footer-minimal__links">
            {links.map(({ key, node }) => (
              <li key={key}>{node}</li>
            ))}
          </ul>
          <div className="footer-minimal__social">
            {socialLinks("footer-minimal__social-link", 18)}
          </div>
        </div>
        <p className="footer-minimal__copyright">{copyright}</p>
      </footer>
    );
  }

  return (
    <footer>
      <div className="container-fluid">
        <div className="row justify-content-between align-items-center">
          {/* Logo */}
          <div className="col-12 col-md-3 text-center text-md-start mb-3 mb-md-0">
            <Link to={"/"} className="navbar-brand">
              <img src={intizaLogoPng} alt="Intiza" />
            </Link>
          </div>

          {/* Links */}
          <div className="col-12 col-md-6 text-center mb-3 mb-md-0">
            <ul className="list-unstyled d-flex flex-md-row justify-content-center mb-0">
              {links.map(({ key, node }) => (
                <li key={key} className="mb-2 mb-md-0 mx-0">
                  {node}
                </li>
              ))}
            </ul>
          </div>

          {/* Redes sociales */}
          <div className="col-12 col-md-3 text-center text-md-end mb-3 mb-md-0">
            <div className="rrss d-flex justify-content-center justify-content-md-end">
              {socialLinks("mx-2", 26)}
            </div>
          </div>
        </div>

        {/* Subfooter */}
        <div className="row">
          <div className="col-12 text-center">
            <div className="subfooter">{copyright}</div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
